import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || (typeof window !== 'undefined' && window.location.port === '3000' ? `http://${window.location.hostname}:8000` : '');

// Lightweight, responsive SVG trends chart for mobile viewports
export const MiniTrendsChart = ({ timeline = [] }) => {
  if (!timeline || timeline.length === 0) {
    return (
      <div style={{
        height: '90px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#64748b',
        fontSize: '11px',
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
        borderRadius: '8px'
      }}>
        No trend activity recorded in the last 14 days.
      </div>
    );
  }

  const width = 340;
  const height = 110;
  const paddingLeft = 24;
  const paddingRight = 12;
  const paddingTop = 12;
  const paddingBottom = 20;

  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;

  const maxVal = Math.max(
    ...timeline.map(t => Math.max(t.views || 0, t.shares || 0, t.bookings || 0, t.clicks || 0)),
    3
  );

  const getX = (idx) => paddingLeft + (idx / Math.max(timeline.length - 1, 1)) * chartWidth;
  const getY = (val) => paddingTop + chartHeight - ((val || 0) / maxVal) * chartHeight;

  // Build smooth bezier path
  const makePath = (dataKey) => {
    return timeline.reduce((acc, point, i) => {
      const x = getX(i);
      const y = getY(point[dataKey]);
      if (i === 0) return `M ${x} ${y}`;
      const prevX = getX(i - 1);
      const prevY = getY(timeline[i - 1][dataKey]);
      const cpX1 = prevX + (x - prevX) / 2;
      const cpX2 = prevX + (x - prevX) / 2;
      return `${acc} C ${cpX1} ${prevY}, ${cpX2} ${y}, ${x} ${y}`;
    }, '');
  };

  const viewsPath = makePath('views');
  const sharesPath = makePath('shares');
  const clicksPath = makePath('clicks');

  const viewsArea = `${viewsPath} L ${getX(timeline.length - 1)} ${paddingTop + chartHeight} L ${getX(0)} ${paddingTop + chartHeight} Z`;

  return (
    <div style={{ width: '100%', userSelect: 'none' }}>
      <svg
        data-testid="card-trends-chart"
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', height: 'auto', overflow: 'visible', display: 'block' }}
      >
        <defs>
          <linearGradient id="mobileViewsGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#38bdf8" stopOpacity="0.0" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {[0, 0.5, 1].map((ratio, idx) => {
          const y = paddingTop + chartHeight * (1 - ratio);
          return (
            <g key={idx}>
              <line
                x1={paddingLeft}
                y1={y}
                x2={width - paddingRight}
                y2={y}
                stroke="rgba(255, 255, 255, 0.08)"
                strokeDasharray={ratio > 0 ? '2 2' : 'none'}
              />
              <text
                x={paddingLeft - 5}
                y={y + 3}
                fill="#64748b"
                fontSize="8"
                textAnchor="end"
                fontFamily="sans-serif"
              >
                {Math.round(maxVal * ratio)}
              </text>
            </g>
          );
        })}

        {/* Area fill */}
        <path d={viewsArea} fill="url(#mobileViewsGradient)" />

        {/* Curves */}
        <path d={viewsPath} fill="none" stroke="#38bdf8" strokeWidth="2.2" strokeLinecap="round" />
        <path d={sharesPath} fill="none" stroke="#c084fc" strokeWidth="1.8" strokeLinecap="round" strokeDasharray="3 2" />
        <path d={clicksPath} fill="none" stroke="#34d399" strokeWidth="2" strokeLinecap="round" />

        {/* Start and end date labels */}
        {timeline.length > 0 && (
          <>
            <text x={paddingLeft} y={height - 4} fill="#64748b" fontSize="8" textAnchor="start">
              {timeline[0].label || timeline[0].date}
            </text>
            <text x={width - paddingRight} y={height - 4} fill="#64748b" fontSize="8" textAnchor="end">
              {timeline[timeline.length - 1].label || timeline[timeline.length - 1].date}
            </text>
          </>
        )}
      </svg>

      {/* Chart Legend */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '14px',
        marginTop: '6px',
        fontSize: '10px',
        fontWeight: '600'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#38bdf8' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#38bdf8' }} />
          <span>Views</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#c084fc' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#c084fc' }} />
          <span>Shares</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#34d399' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#34d399' }} />
          <span>Referral Clicks</span>
        </div>
      </div>
    </div>
  );
};

export default function MobileCardStatsModal({
  publicCode,
  coin,
  onClose
}) {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState('');

  const cardTitle = coin?.card_data?.species || coin?.card_data?.title || coin?.card_name || 'Trophy Card';

  useEffect(() => {
    if (!publicCode) return;
    const fetchAnalytics = async () => {
      setLoading(true);
      setError('');
      try {
        const token = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
        const res = await fetch(`${API_BASE}/api/v1/coin-vault/${publicCode}/analytics?days=14`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'X-Tenant-Domain': typeof window !== 'undefined' ? window.location.hostname : ''
          }
        });
        if (!res.ok) {
          throw new Error('Failed to load card analytics');
        }
        const data = await res.json();
        setAnalytics(data);
      } catch (err) {
        console.error('[MobileCardStatsModal] Error loading card analytics:', err);
        setError(err.message || 'Unable to load stats');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [publicCode]);

  const viewsCount = analytics?.views ?? (coin?.views_count || 0);
  const sharesCount = analytics?.shares ?? (coin?.shares_count || 0);
  const hostClicks = analytics?.host_clicks ?? (coin?.host_clicks_count || (coin?.booking_clicks_count || 0) + (coin?.website_clicks_count || 0) + (coin?.email_clicks_count || 0));
  const conversionRate = analytics?.conversion_rate ?? (viewsCount > 0 ? ((hostClicks / viewsCount) * 100).toFixed(1) : '0.0');

  return (
    <div
      data-testid="mobile-card-stats-modal-backdrop"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10000,
        backgroundColor: 'rgba(5, 8, 15, 0.85)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        padding: 0
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        data-testid="mobile-card-stats-sheet"
        style={{
          width: '100%',
          maxWidth: '480px',
          maxHeight: '90dvh',
          backgroundColor: '#0f172a',
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 -10px 40px rgba(0, 0, 0, 0.7)',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box'
        }}
      >
        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px 0' }}>
          <div style={{ width: '40px', height: '4px', borderRadius: '2px', backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />
        </div>

        {/* Modal Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 18px 14px 18px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '15px' }}>📊</span>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#ffffff', letterSpacing: '-0.3px' }}>
                Card Performance
              </h3>
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
              {cardTitle} &bull; <span style={{ color: '#38bdf8', fontWeight: 600 }}>#{publicCode}</span>
            </div>
          </div>

          <button
            type="button"
            data-testid="close-card-stats-btn"
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#cbd5e1',
              cursor: 'pointer'
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content body */}
        <div style={{ padding: '16px 18px 30px 18px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {loading && (
            <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
              <div style={{
                width: '28px',
                height: '28px',
                border: '3px solid rgba(56, 189, 248, 0.2)',
                borderTopColor: '#38bdf8',
                borderRadius: '50%',
                margin: '0 auto 10px auto',
                animation: 'spin 0.8s linear infinite'
              }} />
              <span>Loading engagement analytics...</span>
            </div>
          )}

          {error && (
            <div style={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.25)',
              borderRadius: '10px',
              padding: '12px',
              color: '#f87171',
              fontSize: '12px'
            }}>
              {error}
            </div>
          )}

          {/* 4 Quick Metric Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '10px'
          }}>
            {/* Views */}
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '14px',
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#94a3b8', fontSize: '11px', fontWeight: 600 }}>
                <span>Card Views</span>
                <span style={{ color: '#38bdf8' }}>👁️</span>
              </div>
              <div style={{ fontSize: '22px', fontWeight: 900, color: '#f8fafc', letterSpacing: '-0.5px' }}>
                {viewsCount}
              </div>
              <div style={{ fontSize: '10px', color: '#64748b' }}>
                Scans & shared opens
              </div>
            </div>

            {/* Shares */}
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '14px',
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#94a3b8', fontSize: '11px', fontWeight: 600 }}>
                <span>Shares</span>
                <span style={{ color: '#c084fc' }}>↗️</span>
              </div>
              <div style={{ fontSize: '22px', fontWeight: 900, color: '#f8fafc', letterSpacing: '-0.5px' }}>
                {sharesCount}
              </div>
              <div style={{ fontSize: '10px', color: '#64748b' }}>
                Social & message shares
              </div>
            </div>

            {/* Click-throughs */}
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '14px',
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#94a3b8', fontSize: '11px', fontWeight: 600 }}>
                <span>Referrals</span>
                <span style={{ color: '#34d399' }}>🎯</span>
              </div>
              <div style={{ fontSize: '22px', fontWeight: 900, color: '#f8fafc', letterSpacing: '-0.5px' }}>
                {hostClicks}
              </div>
              <div style={{ fontSize: '10px', color: '#64748b' }}>
                Web, booking, & email
              </div>
            </div>

            {/* Engagement Rate */}
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '14px',
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#94a3b8', fontSize: '11px', fontWeight: 600 }}>
                <span>Engagement</span>
                <span style={{ color: '#fbbf24' }}>⚡</span>
              </div>
              <div style={{ fontSize: '22px', fontWeight: 900, color: '#34d399', letterSpacing: '-0.5px' }}>
                {conversionRate}%
              </div>
              <div style={{ fontSize: '10px', color: '#64748b' }}>
                Action conversion rate
              </div>
            </div>
          </div>

          {/* 14-Day Activity Trends Chart */}
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.02)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '16px',
            padding: '14px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#f8fafc' }}>
                14-Day Engagement Trend
              </span>
              <span style={{ fontSize: '10px', color: '#94a3b8', backgroundColor: 'rgba(255, 255, 255, 0.06)', padding: '2px 6px', borderRadius: '4px' }}>
                Daily Activity
              </span>
            </div>

            <MiniTrendsChart timeline={analytics?.timeline || []} />
          </div>

          {/* Referral Breakdown Details */}
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.02)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '16px',
            padding: '14px'
          }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#f8fafc', marginBottom: '10px' }}>
              Referral Destinations
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px' }}>
                <span style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  🌐 Outfitter Website
                </span>
                <span style={{ fontWeight: 700, color: '#f8fafc' }}>
                  {analytics?.website_clicks ?? (coin?.website_clicks_count || 0)} clicks
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px' }}>
                <span style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  📅 Lodge / Booking System
                </span>
                <span style={{ fontWeight: 700, color: '#f8fafc' }}>
                  {analytics?.booking_clicks ?? (coin?.booking_clicks_count || 0)} inquiries
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px' }}>
                <span style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  ✉️ Direct Email
                </span>
                <span style={{ fontWeight: 700, color: '#f8fafc' }}>
                  {analytics?.email_clicks ?? (coin?.email_clicks_count || 0)} clicks
                </span>
              </div>
            </div>
          </div>

          {/* Recent Event Logs */}
          {analytics?.logs && analytics.logs.length > 0 && (
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '16px',
              padding: '14px'
            }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#f8fafc', marginBottom: '8px' }}>
                Recent Activity Log
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {analytics.logs.slice(0, 5).map((log, idx) => (
                  <div
                    key={log.id || idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '11px',
                      padding: '6px 8px',
                      borderRadius: '6px',
                      backgroundColor: 'rgba(255, 255, 255, 0.02)'
                    }}
                  >
                    <span style={{ color: '#cbd5e1', fontWeight: 600 }}>
                      {log.label || log.event_type}
                    </span>
                    <span style={{ color: '#64748b', fontSize: '10px' }}>
                      {log.timestamp ? new Date(log.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
